import { useState } from 'react';
import type { CsvRow, SortCol, SortDir } from '../../tools/types';
import { formatDate, initials, processLookupLink } from '../../utils/csv';
import InlineGraph from "./InlineGraph";
import styles from './DataTable.module.scss';
import StandardLink from '../../tools/StandardLink';

interface Props {
  rows: CsvRow[];
  extraCols: string[];
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
}

function SortArrow({ col, sortCol, sortDir }: { col: string; sortCol: string; sortDir: SortDir }) {
  if (col !== sortCol) return <span className={styles.arrow} aria-hidden="true">↕</span>;
  return <span className={`${styles.arrow} ${styles.active}`} aria-hidden="true">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function validateNumber(num) {
  if (num) {
    return Number(num);
  }
  return undefined
}

const DESC_TRUNCATE = 50;

function DescriptionCell({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);

  // allow for only one usage of > and < to be altered; removing all others as safety percation
  const updatedValue = (
      value.indexOf("::gt::") > -1 ||
      value.indexOf("::lt::") > -1
  ) ? 
    value.replace("::gt::", ">").replace("::lt::", "<").replace(/::(gt|lt)::/g, "") :
    value || '—';
  
  const isTruncatable = updatedValue.length > DESC_TRUNCATE;

  return (
    <div
      className={`${styles.descCell} ${isTruncatable ? styles.descTruncatable : ''} ${expanded ? styles.descExpanded : ''}`}
      onClick={isTruncatable ? (e) => { e.stopPropagation(); setExpanded(prev => !prev); } : undefined}
      role={isTruncatable ? 'button' : undefined}
      aria-expanded={isTruncatable ? expanded : undefined}
      title={isTruncatable && !expanded ? updatedValue : undefined}
    >
      <span className={styles.descText}>{updatedValue || '—'}</span>
      {isTruncatable && (
        <span className={styles.descToggle} aria-hidden="true">
          {expanded ? '↑ less' : '↓ more'}
        </span>
      )}
    </div>
  );
}

export default function DataTable({ rows, extraCols, sortCol, sortDir, onSort }: Props) {
  const allCols: Array<{ key: string; label: string; sortable: boolean }> = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'type', label: 'type', sortable: true },
    ...extraCols.map(c => ({ key: c, label: c, sortable: false })),
  ];

  return (
    <div className={styles.scroll}>
      <table className={styles.table} aria-label="CSV data">
        <thead>
          <tr>
            {allCols.map(col => (
              <th
                key={col.key}
                className={[
                  col.sortable        ? styles.sortable : '',
                  sortCol === col.key ? styles.activeTh : '',
                  col.key.toLowerCase() === 'description' ? styles.deptTh : '',
                ].join(' ')}
                onClick={col.sortable ? () => onSort(col.key as SortCol) : undefined}
                aria-sort={
                  sortCol === col.key
                    ? sortDir === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                {col.label}
                {col.sortable && (
                  <SortArrow col={col.key} sortCol={sortCol} sortDir={sortDir} />
                )}
              </th>
            ))}
              <th>Graph</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={allCols.length+1} className={styles.empty}>
                No matching rows found.
              </td>
            </tr>
          ) : (
            rows.map(row => (
              <tr key={row._id} className={styles.row}>
                <td>
                  <div className={styles.nameCell}>
                    <span className={styles.avatar} aria-hidden="true">
                      {initials(row.name)}
                    </span>
                    { (row.name !== undefined) ?
                      <StandardLink href={processLookupLink(row.name)} isNewWindow={true}>{row.name}</StandardLink> :
                      <em className={styles.emptyVal}>(empty)</em>}
                  </div>
                </td>
                <td className={styles.dateCell}>
                  {formatDate(row.date)}
                </td>
                <td className={styles.typeCell}>
                  { (row.type !== undefined) ?
                      <StandardLink href={processLookupLink(row.type)} isNewWindow={true}>{row.type}</StandardLink> :
                      <em className={styles.emptyVal}>(empty)</em>}
                </td>
                {extraCols.map(c => (
                  <td
                    key={c}
                    className={`${styles.extraCell} ${c.toLowerCase() === 'department' ? styles.deptTd : ''}`}
                  >
                    {c.toLowerCase() === 'description'
                      ? <DescriptionCell value={row.extra[c] || ''} />
                      : row.extra[c] || '—'
                    }
                  </td>
                ))}
                {
                (
                  (row.extra["Result"] || row.extra["Last"]) && 
                  (row.extra["Low"] || row.extra["High"])
                ) ? (
                <td><InlineGraph result={(row.extra["Result"]) ? validateNumber(row.extra["Result"]) : validateNumber(row.extra["Last"])} rangeLow={validateNumber(row.extra["Low"])} rangeHigh={validateNumber(row.extra["High"])} /></td>
                ) : (
                  <td><span>not enough data to graph</span></td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
